import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon, XIcon } from 'lucide-react';
import { Project } from '../lib/types';
interface ProjectModalProps {
  project: Project;
  onClose: () => void;
}
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.15 + i * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
function ContentRow({
  label,
  children,
  index
}: {
  label: string;
  children: React.ReactNode;
  index: number;
}) {
  if (!children) return null;
  return <motion.div custom={index} variants={sectionVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14" data-id="element-297">
      <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-1" data-id="element-298">
        {label}
      </h3>
      <div className="font-mono text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl whitespace-pre-line" data-id="element-299">
        {children}
      </div>
    </motion.div>;
}
export function ProjectModal({
  project,
  onClose
}: ProjectModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  // Use projectImages first, fall back to gallery blocks, then cover image
  const projectImages = (project as any).projectImages || [];
  const galleryBlockImages = project.blocks?.filter(b => b.type === 'gallery').flatMap(b => b.content?.images || []) || [];
  const displayImages: {
    url: string;
    alt?: string;
    caption?: string;
  }[] = projectImages.length > 0 ? projectImages.filter((img: any) => img.url) : galleryBlockImages.length > 0 ? galleryBlockImages.filter((img: any) => img.url) : project.coverImage ? [{
    url: project.coverImage,
    alt: project.title,
    caption: ''
  }] : [];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 bg-background overflow-y-auto" data-id="element-300">
      {/* Close button */}
      <button onClick={onClose} className="fixed top-6 right-6 md:top-8 md:right-12 z-50 p-2 text-muted-foreground hover:text-foreground transition-colors duration-200" aria-label="Close" data-id="element-301">
        <XIcon className="w-6 h-6" data-id="element-302" />
      </button>

      <div className="min-h-screen flex flex-col" data-id="element-303">
        {/* Main content area */}
        <div className="flex-1 w-full max-w-[960px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-id="element-304">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center gap-2 mb-8 md:mb-10" data-id="element-305">
            <span className="font-mono text-xs text-primary tracking-[0.2em] uppercase" data-id="element-306">
              // Project {project.num}
            </span>
            <span className="font-mono text-xs text-muted-foreground" data-id="element-307">
              {'>'}
            </span>
            <span className="font-mono text-xs text-muted-foreground tracking-widest" data-id="element-308">
              {project.year}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="font-display text-[clamp(2.5rem,8vw,7rem)] font-bold leading-[0.9] tracking-tighter mb-8 md:mb-10 text-foreground" data-id="element-309">
            {project.title}
          </motion.h1>

          {/* Tags */}
          <motion.div custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="flex flex-wrap gap-3 mb-16 md:mb-20" data-id="element-310">
            <span className="font-mono text-xs px-4 py-2 border border-border text-foreground/80" data-id="element-311">
              {project.role}
            </span>
            {project.tools?.length > 0 && <span className="font-mono text-xs px-4 py-2 border border-border text-foreground/80" data-id="element-312">
                {project.tools.join(', ')}
              </span>}
          </motion.div>

          {/* Overview */}
          <ContentRow label="Overview" index={3} data-id="element-313">
            {project.summary}
          </ContentRow>

          <div className="border-t border-border" data-id="element-314" />

          {/* Challenge / Approach / Outcome */}
          <ContentRow label="The Challenge" index={4} data-id="element-315">
            {project.challenge}
          </ContentRow>

          <ContentRow label="Approach" index={5} data-id="element-316">
            {project.approach}
          </ContentRow>

          <ContentRow label="Outcome" index={6} data-id="element-317">
            {project.outcome}
          </ContentRow>

          <div className="border-t border-border" data-id="element-318" />

          {/* Key Highlights */}
          {project.metrics && project.metrics.length > 0 && <motion.div custom={7} variants={sectionVariants} initial="hidden" animate="visible" className="py-12 md:py-16" data-id="element-319">
              <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium mb-8" data-id="element-320">
                Key Metrics & Highlights
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-321">
                {project.metrics.map((metric, idx) => <div key={idx} className="bg-card border border-border p-6 md:p-8" data-id="element-322">
                    <span className="font-display text-3xl md:text-4xl font-bold text-muted-foreground/40 leading-none block mb-4" data-id="element-323">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <p className="font-mono text-sm text-foreground leading-relaxed" data-id="element-324">
                      {metric}
                    </p>
                  </div>)}
              </div>
            </motion.div>}

          {/* Images section */}
          {displayImages.length > 0 && <>
              <div className="border-t border-border" data-id="element-325" />
              <motion.div custom={8} variants={sectionVariants} initial="hidden" animate="visible" className="py-12 md:py-16" data-id="element-326">
                <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium mb-8" data-id="element-327">
                  Gallery
                </h3>
                <div className="space-y-6" data-id="element-328">
                  {displayImages.map((img, idx) => <div key={idx} className="overflow-hidden border border-border bg-card" data-id="element-329">
                      <img src={img.url} alt={img.alt || `${project.title} screenshot ${idx + 1}`} className="w-full h-auto" loading="lazy" data-id="element-330" />
                      {img.caption && <div className="px-4 py-3 border-t border-border" data-id="element-331">
                          <p className="font-mono text-xs text-muted-foreground" data-id="element-332">
                            {img.caption}
                          </p>
                        </div>}
                    </div>)}
                </div>
              </motion.div>
            </>}

          <div className="border-t border-border" data-id="element-333" />

          {/* Footer navigation */}
          <motion.div custom={9} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center justify-between py-8 md:py-10" data-id="element-334">
            <button onClick={onClose} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" aria-label="Back to projects" data-id="element-335">
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" data-id="element-336" />
              Back to Projects
            </button>

            {project.liveUrl && project.liveUrl !== '#' && <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary hover:text-foreground transition-colors duration-300" data-id="element-337">
                View Live Project
                <ArrowUpRightIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" data-id="element-338" />
              </a>}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}